import { useState } from 'react';
import {
  Button,
  IconButton,
  PersonAvatar,
  IconTile,
  InputTextarea,
} from '@teo/components';
import {
  Training,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  ChevronRight,
  CloseCircleFilled,
  ArrowLeft,
  DuoToneTrajectoryFilled,
} from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { axios } from '@/axios';
import { config } from '@/config';
import { toPercent } from '@/util';
import RedoModalLp from '@/components/LearningPath/Correction/RedoModalLp';

function CorrectionEvaluation({
  node,
  studentId,
  setEvaluationContent,
  student,
  learningpath,
  Icon,
  refetchResult,
}) {
  const { t } = useTranslation();

  const [action, setAction] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [isOpenRedoModal, setIsOpenRedoModal] = useState(false);

  return (
    <div className="fixed top-0 left-0 z-10 h-screen w-screen bg-white">
      <div className="flex items-center justify-between border-b border-solid border-grey-02 bg-white px-5 py-4">
        <div className="flex w-full flex-row gap-4 text-grey-07 sm:items-center">
          <div className="flex items-center">
            <IconButton
              className="my-auto"
              Icon={ArrowLeft}
              onClick={() =>
                setEvaluationContent({
                  open: false,
                  node: null,
                  studentId: null,
                  Icon: null,
                })
              }
              variant="outline"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-primary-01 p-1 ">
              <DuoToneTrajectoryFilled className="h-4 w-4 fill-primary-05 text-primary-03" />
            </div>
            <p className="text-sm text-black">
              {learningpath?.lpgroupName || learningpath?.lpName}
            </p>
          </div>
          <div>
            <ChevronRight className="h-4 w-4 text-grey-07" />
          </div>
          <div className="flex items-center">
            <PersonAvatar
              inline
              id={student?.studentId}
              name={student?.username}
              imageSrc={student?.picture}
            ></PersonAvatar>
            <p className="text-sm text-black">{student?.username}</p>
          </div>
          <div>
            <ChevronRight className="h-4 w-4 text-grey-07" />
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-tint-purple-light p-1 ">
              <Evaluation className="h-4 w-4 text-tint-purple-dark" />
            </div>
            <p className="text-sm text-black">
              {t('pages.lp_correction.lp_next_steps')}
            </p>
          </div>
        </div>
      </div>
      <div className="h-full max-h-[calc(100vh_-_146px)] w-full overflow-y-auto pb-3">
        <div className="flex w-full">
          <div className="mx-auto flex w-full max-w-xl flex-col gap-6 py-8 px-4">
            <div className="flex items-center gap-3">
              <div>
                <IconTile Icon={Evaluation} color="purple" size="small_md" />
              </div>
              <div>
                <p className="text-sm text-grey-05">
                  {t('pages.lp_correction.lp_evaluation')}
                </p>
                <p className={`text-base font-semibold text-black`}>
                  {t('pages.lp_correction.lp_hold')}
                </p>
              </div>
            </div>
            <div
              className={`flex w-full flex-col items-center rounded-xl border border-error-02 p-3`}
            >
              <div className="flex w-full items-center">
                <IconTile
                  Icon={
                    node?.subType === 'training'
                      ? Training
                      : node?.subType === 'learningpath'
                      ? Icon
                      : node?.subType === 'milestone'
                      ? Flag
                      : node?.subType === 'evaluation'
                      ? Evaluation
                      : node?.subType === 'course'
                      ? DuoToneCourseFilled
                      : node?.subType === 'assessment'
                      ? DuoToneAssessmentFilled
                      : node?.subType === 'leervacature'
                      ? DuoToneVacancyFilled
                      : DuoToneCourseFilled
                  }
                  imageUrl={node?.picture}
                  color={
                    node?.subType === 'training'
                      ? 'green'
                      : node?.subType === 'learningpath'
                      ? 'dark_blue'
                      : node?.subType === 'milestone'
                      ? 'blue'
                      : node?.subType === 'evaluation'
                      ? 'purple'
                      : node?.subType === 'course'
                      ? 'yellow'
                      : node?.subType === 'assessment'
                      ? 'red'
                      : node?.subType === 'leervacature'
                      ? 'yellow'
                      : 'yellow'
                  }
                  size="small_md"
                  className={
                    node?.subType === 'course'
                      ? 'fill-primary-06 text-primary-01'
                      : node?.subType === 'assessment'
                      ? 'fill-tint-red-dark text-tint-red-light'
                      : node?.subType === 'leervacature' &&
                        'fill-error-04 text-error-04'
                  }
                />
                <div className="flex flex-1 flex-col gap-1 px-3">
                  <p className="text-xs text-grey-06">
                    {node?.subType === 'training'
                      ? t('pages.learning_path.training')
                      : node?.subType === 'learningpath'
                      ? t('pages.learning_path.theme')
                      : node?.subType === 'milestone'
                      ? t('pages.learning_path.milestone')
                      : node?.subType === 'evaluation'
                      ? t('pages.learning_path.evaluation')
                      : node?.subType === 'course'
                      ? t('links.course')
                      : node?.subType === 'assessment'
                      ? t('links.assessment')
                      : node?.subType === 'leervacature'
                      ? t('links.vacancy')
                      : t('links.course')}
                  </p>
                  <p
                    className={`max-w-[225px] break-words text-left text-sm font-medium text-black line-clamp-2 `}
                  >
                    {node?.name}
                  </p>
                </div>
                {node?.minScore === 1 ? (
                  <CloseCircleFilled className="ml-7 h-8 w-8 text-error-04" />
                ) : (
                  <div className="ml-auto flex items-center gap-1 text-sm text-error-04">
                    {t('columns.score')}:{' '}
                    <span className="font-semibold">
                      {toPercent(node.score, node.maxScore) && node.score
                        ? toPercent(node.score, node.maxScore)
                        : 0}
                      %
                    </span>
                    <CloseCircleFilled className="ml-7 h-8 w-8 text-error-04" />
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2 flex flex-col gap-2">
              <p className="mb-1 text-base font-semibold">
                {t('pages.lp_correction.follow_up_actions')}
              </p>
              <Button
                className={`flex basis-1/2 items-center border text-sm shadow-none ${
                  action === 're-enter' ? 'border-secondary-04' : ''
                }`}
                onClick={() => {
                  node?.type === 'course'
                    ? setIsOpenRedoModal(true)
                    : setAction('re-enter');
                }}
                variant="outline"
                size="lg"
              >
                <img
                  src="/images/icons/re_take.svg"
                  alt="re_take"
                  className="mr-3 w-5"
                />
                {t('pages.lp_correction.re_take')}
              </Button>
              <Button
                className={`flex basis-1/2 items-center border text-sm shadow-none ${
                  action === 'end' ? 'border-secondary-04' : ''
                }`}
                onClick={() => {
                  setAction('end');
                }}
                variant="outline"
                size="lg"
              >
                <img
                  src="/images/icons/lp_end.svg"
                  alt="end"
                  className="mr-3 w-5"
                />
                {t('pages.lp_correction.end_lp')}
              </Button>
            </div>

            <div className="mt-2 flex w-full flex-col">
              <h4 className="mb-1 text-sm font-semibold text-black">
                {t('pages.lp_training_correction.remarks')}
              </h4>
              <div>
                <InputTextarea
                  id="remarks"
                  placeholder={t(`pages.lp_training_correction.type_remarks`)}
                  onChange={(ev) => setRemarks(ev.target.value)}
                  value={remarks}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className=" flex w-full border-t border-grey-02">
        <div className="mx-auto flex w-full max-w-xl justify-between px-4 py-5">
          <Button
            variant="ghost"
            onClick={() =>
              setEvaluationContent({
                open: false,
                node: null,
                studentId: null,
                Icon: null,
              })
            }
          >
            {t('button.cancel')}
          </Button>

          <Button
            disabled={!action}
            onClick={async () => {
              const promises = [];
              if (action === 'end') {
                promises.push(
                  axios.put(
                    `${config.backend}/lpfollow/${learningpath?.id}/${studentId}`,
                    {
                      isBlocked: true,
                    }
                  )
                );
                Promise.allSettled(promises).then(() => {
                  setEvaluationContent({
                    open: false,
                    node: null,
                    studentId: null,
                    Icon: null,
                  });
                });
              } else {
                if (node?.type === 'course') {
                  promises.push(
                    axios.post(
                      `${config.teodoc}/corrections/reset/${
                        node?.objectId
                      }/${studentId}/?keepResults=${
                        action === 'complete' ? true : false
                      }`
                    )
                  );
                  await Promise.allSettled(promises).then(() => {
                    refetchResult();
                    setEvaluationContent({
                      open: false,
                      node: null,
                      studentId: null,
                      Icon: null,
                    });
                  });
                } else {
                  promises.push(
                    axios.put(
                      `${config.backend}/trainingfollow/${node?.objectId}/${studentId}`,
                      {
                        progress: 0,
                        isCorrected: false,
                        score: 0,
                        maxScore: 1,
                        feedback: remarks && remarks,
                      }
                    )
                  );
                  await Promise.allSettled(promises).then(() => {
                    refetchResult();
                    setEvaluationContent({
                      open: false,
                      node: null,
                      studentId: null,
                      Icon: null,
                    });
                  });
                }
              }
            }}
          >
            {t('pages.lp_training_correction.submit_type', {
              type: t(`pages.lp_landing.evaluation`),
            })}
          </Button>
        </div>
      </div>
      {isOpenRedoModal && (
        <RedoModalLp
          closeModal={() => setIsOpenRedoModal(false)}
          setIsOpenRedoModal={setIsOpenRedoModal}
          setAction={setAction}
        />
      )}
    </div>
  );
}

export default CorrectionEvaluation;
